import type { Persona } from '@/types';

/** Any level of ATX heading (`#` through `######`) — headings make poor card blurbs, they're just section titles. */
function isHeadingLine(line: string): boolean {
	return /^#{1,6}\s/.test(line.trim());
}

/** Strips a leading bullet marker and bold markers from a content line, for plain-text display. */
function stripContentMarkers(line: string): string {
	return line
		.trim()
		.replace(/^[-*]\s*/, '')
		.replace(/\*\*/g, '')
		.trim();
}

/** A guide line as a client reads it in the "What changed" diff, without markdown syntax. */
export interface ReadableDiffLine {
	kind: 'heading' | 'bullet' | 'text';
	text: string;
}

/** Drops inline markdown: links keep their text; bold, italic and code markers go. */
function stripInlineMarkdown(text: string): string {
	return text
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/(\*\*|__)(.+?)\1/g, '$2')
		.replace(/\*(\S(?:.*?\S)?)\*/g, '$1')
		.replace(/`([^`]+)`/g, '$1')
		.trim();
}

/**
 * Converts one raw guide line (markdown) to readable text: headings, bullets, and table rows
 * as "cell · cell". `null` for a line with no text of its own, such as a table's `| --- |` rule.
 */
export function toReadableDiffLine(line: string): ReadableDiffLine | null {
	const trimmed = line.trim();
	if (/^[|\s:-]+$/.test(trimmed) && trimmed.includes('-')) return null;
	if (isHeadingLine(trimmed)) return { kind: 'heading', text: stripInlineMarkdown(trimmed.replace(/^#{1,6}\s+/, '')) };
	if (trimmed.startsWith('|')) {
		const cells = trimmed
			.replace(/^\||\|$/g, '')
			.split('|')
			.map(stripInlineMarkdown)
			.filter((cell) => cell.length > 0);
		return { kind: 'text', text: cells.join(' · ') };
	}
	const bullet = /^[-*+]\s+(.*)$/.exec(trimmed);
	if (bullet) return { kind: 'bullet', text: stripInlineMarkdown(bullet[1] ?? '') };
	return { kind: 'text', text: stripInlineMarkdown(trimmed.replace(/^>\s?/, '')) };
}

/**
 * `Persona.summary` is typed as `string`, but a doc generated before the backend switched
 * `summary` from the old structured `{tone,greetings,...}` shape to a raw markdown string
 * still has the old object in the database — the type change doesn't rewrite existing rows.
 * Guard against that here rather than crashing on `.split`/`.trim` of a non-string value;
 * it just reads as "no summary yet" until that persona is next generated/refined.
 */
export function asSummaryText(summary: unknown): string {
	return typeof summary === 'string' ? summary : '';
}

export function getPersonaDescription(persona: Persona): string {
	// Skip heading lines (`## Summary`, `### Tone`, ...) — the first real bullet under them
	// (e.g. "Warmly casual and genuinely helpful") is what actually reads as a description.
	const firstLine = asSummaryText(persona.summary)
		.split('\n')
		.filter((line) => line.trim().length > 0 && !isHeadingLine(line))
		.map(stripContentMarkers)
		.find((line) => line.length > 0);
	if (firstLine) return firstLine;
	return persona.userId == null
		? 'A unified brand Persona to maintain consistency across all platforms.'
		: "This is a tailored overview of this agent's actions, reflecting their unique historical ticket records.";
}
